import { useNavigate } from 'react-router-dom';

import { UserGoal } from 'src/common/enums';
import type { OccupationDetailsFormData } from 'src/common/interfaces';
import { usePostOccupationDetailsMutation } from 'src/common/slices/occupation-details.slice';
import { useUpdateDemographicsMutation } from 'src/common/slices/user/demographics.slice';
import { useEnrollInPathMutation } from 'src/features/path-outline/path-outline.slice';
import {
  PRE_SIGNUP_STATE_STORAGE_KEY,
  type PreSignupOnboardingState,
} from 'src/features/pre-signup-onboarding/pre-signup-onboarding.slice';

import { useDelaySignupExperiment } from './use-delay-signup-experiment';

/**
 * A hook to process the state stored in localStorage from the pre-signup onboarding flow.
 * It handles updating user demographics, posting occupation details, and navigating the user
 * to the appropriate page (`/hacktivities` or `/dashboard`) upon completion.
 *
 * @returns An `handlePreSignupFlow` function.
 */
export const useProcessPreSignupState = () => {
  const navigate = useNavigate();
  const [updateDemographics] = useUpdateDemographicsMutation();
  const [postOccupationDetails] = usePostOccupationDetailsMutation();
  const [enrollInPath] = useEnrollInPathMutation();
  const isDelaySignupEnabled = useDelaySignupExperiment();

  /**
   * Processes the stored pre-signup state and navigates accordingly.
   * Returns true if pre-signup onboarding was completed (navigation occurred), false otherwise.
   */
  const handlePreSignupFlow = async (): Promise<boolean> => {
    // Early return if delay signup experiment is not enabled
    if (!isDelaySignupEnabled) {
      return false;
    }

    // Early return if no pre-signup state is stored
    const storedItem = localStorage.getItem(PRE_SIGNUP_STATE_STORAGE_KEY);
    if (!storedItem) {
      return false;
    }

    try {
      const { state: preSignupState } = JSON.parse(storedItem) as {
        state: PreSignupOnboardingState;
      };

      const { demographics, occupationDetails, selectedPath, redirectUrl } = preSignupState;

      // Early return if no demographics data (required for the flow)
      if (!demographics) {
        return false;
      }

      // Execute all operations in parallel since they're independent
      const promises = [
        updateDemographics(demographics).unwrap(),
        enrollInPath(selectedPath ?? 'presecurity').unwrap(),
      ];

      // Add occupation details update if we have the data
      if (occupationDetails) {
        const processedOccupationDetails: OccupationDetailsFormData = {
          ...occupationDetails,
          businessEmail: occupationDetails.businessEmail ?? '',
          companyName: occupationDetails.companyName ?? '',
          institutionType: occupationDetails.institutionType ?? '',
          institutionName: occupationDetails.institutionName ?? '',
          jobTitle: occupationDetails.jobTitle ?? '',
        };

        promises.push(postOccupationDetails(processedOccupationDetails).unwrap());
      }

      await Promise.all(promises);

      // Navigate based on user goal - but only if they should go to hacktivities
      if (demographics.goal && [UserGoal.EVALUTE_THM, UserGoal.TRAIN_TEAM].includes(demographics.goal)) {
        navigate('/hacktivities');
        return true;
      }

      // If we have a stored redirect URL, use that to navigate to the room
      if (redirectUrl) {
        window.location.assign(redirectUrl);
        return true;
      }

      // Since user was enrolled in a path, redirect to paths page instead of dashboard
      navigate('/paths');
      return true;
    } catch (error) {
      console.error('Failed to process pre-signup state:', error);
      return false;
    } finally {
      // Always clean up localStorage to prevent infinite loops
      localStorage.removeItem(PRE_SIGNUP_STATE_STORAGE_KEY);
    }
  };

  return { handlePreSignupFlow };
};
